'use client';

import { Check, Copy, Terminal } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { cn } from '../../utils/cn';

/**
 * ArticleCodeBlock — the dark code surface used by the docs and blog renderers.
 *
 * ACCEPTED DS EXCEPTION: this is the one place raw color values are allowed.
 * DS Button themes are light-surface only, so the dark surface + its copy
 * control use raw white-on-dark values and a minimal accessible <button>.
 */
export const ArticleCodeBlock = ({
	code,
	language,
}: {
	code: string;
	language?: string;
}): React.JSX.Element => {
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(code);
			setCopied(true);
			toast.success('Copied to clipboard');
			setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error('Could not copy to clipboard');
		}
	};

	const label = language ? language : 'Code';

	return (
		<div className="my-[26px] overflow-hidden rounded-md border border-neutral/10 bg-[#0D0E0F]">
			<div className="flex items-center justify-between border-b border-white/[0.08] px-[14px] py-[9px]">
				<div className="flex items-center gap-2 text-white/55">
					<Terminal className="size-[14px]" strokeWidth={2} />
					<span className="text-[12px] font-semibold capitalize">{label}</span>
				</div>
				<button
					type="button"
					onClick={handleCopy}
					aria-label="Copy"
					className="flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[12px] font-semibold text-white/55 transition-colors hover:text-white/80 focus-visible:outline focus-visible:outline-1 focus-visible:outline-white/40"
				>
					{copied ? (
						<Check className="size-[13px]" strokeWidth={2} />
					) : (
						<Copy className="size-[13px]" strokeWidth={2} />
					)}
					{copied ? 'Copied' : 'Copy'}
				</button>
			</div>
			<pre className="overflow-x-auto px-4 py-[14px]">
				<code
					className={cn(
						'block font-mono text-[12.5px] leading-[1.75] text-white/90',
						language && `language-${language}`,
					)}
				>
					{code}
				</code>
			</pre>
		</div>
	);
};
